/**
 * Data Cleanup Utilities for Quila
 * Helps clean up duplicate or corrupted data in localStorage
 */

// Data keys used throughout the application
const DATA_KEYS = {
  COLLEGE_LIST: 'myCollegeList',
  ACTIVITIES: 'collegeActivities',
  ACADEMIC_PROFILE: 'academicProfile',
  USER_PROFILE: 'userProfile',
  USER_PREFERENCES: 'userPreferences',
  USER_GOALS: 'userGoals',
  USER_ANALYTICS: 'userAnalytics',
  PATHWAY_PLANNER_DRAFT: 'pathwayPlannerDraft',
  CONSOLIDATED_DATA: 'quila_user_data',
  BACKUP_DATA: 'quila_user_data_backup',
  BACKUPS: 'quila_backups'
};

/**
 * Remove duplicate activities from localStorage
 * @returns {object} Cleanup results
 */
export const removeDuplicateActivities = () => {
  try {
    const activities = JSON.parse(localStorage.getItem(DATA_KEYS.ACTIVITIES)) || [];
    const originalCount = activities.length;
    
    // Remove duplicates based on name and organization
    const uniqueActivities = activities.filter((activity, index, self) => 
      index === self.findIndex(a => 
        a.name === activity.name && 
        a.organization === activity.organization
      )
    );
    
    // Remove duplicates based on ID
    const finalActivities = uniqueActivities.filter((activity, index, self) => 
      index === self.findIndex(a => a.id === activity.id)
    );
    
    // Save cleaned data
    localStorage.setItem(DATA_KEYS.ACTIVITIES, JSON.stringify(finalActivities));
    
    const removedCount = originalCount - finalActivities.length;
    
    console.log(`[DataCleanup] Removed ${removedCount} duplicate activities`);
    console.log(`[DataCleanup] Original: ${originalCount}, Final: ${finalActivities.length}`);
    
    return {
      success: true,
      originalCount,
      finalCount: finalActivities.length,
      removedCount
    };
  } catch (error) {
    console.error('[DataCleanup] Error removing duplicate activities:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Remove duplicate colleges from localStorage
 * @returns {object} Cleanup results
 */
export const removeDuplicateColleges = () => {
  try {
    const colleges = JSON.parse(localStorage.getItem(DATA_KEYS.COLLEGE_LIST)) || [];
    const originalCount = colleges.length;
    
    // Remove duplicates based on name
    const uniqueColleges = colleges.filter((college, index, self) => 
      index === self.findIndex(c => c.name === college.name)
    );
    
    // Save cleaned data
    localStorage.setItem(DATA_KEYS.COLLEGE_LIST, JSON.stringify(uniqueColleges));
    
    const removedCount = originalCount - uniqueColleges.length;
    
    console.log(`[DataCleanup] Removed ${removedCount} duplicate colleges`);
    console.log(`[DataCleanup] Original: ${originalCount}, Final: ${uniqueColleges.length}`);
    
    return {
      success: true,
      originalCount,
      finalCount: uniqueColleges.length,
      removedCount
    };
  } catch (error) {
    console.error('[DataCleanup] Error removing duplicate colleges:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Clean up all data - remove duplicates and fix corrupted entries
 * @returns {object} Cleanup results
 */
export const cleanupAllData = () => {
  console.log('[DataCleanup] Starting comprehensive data cleanup...');
  
  const results = {
    activities: removeDuplicateActivities(),
    colleges: removeDuplicateColleges(),
    timestamp: new Date().toISOString()
  };
  
  // Dispatch events to notify components
  if (results.activities.success) {
    window.dispatchEvent(new CustomEvent('collegeActivitiesUpdated'));
  }
  if (results.colleges.success) {
    window.dispatchEvent(new Event('collegeListUpdated'));
  }
  
  console.log('[DataCleanup] Data cleanup completed:', results);
  return results;
};

/**
 * Get data statistics for debugging
 * @returns {object} Data statistics
 */
export const getDataStats = () => {
  try {
    const activities = JSON.parse(localStorage.getItem(DATA_KEYS.ACTIVITIES)) || [];
    const colleges = JSON.parse(localStorage.getItem(DATA_KEYS.COLLEGE_LIST)) || [];
    const academicProfile = JSON.parse(localStorage.getItem(DATA_KEYS.ACADEMIC_PROFILE)) || {};
    const userProfile = JSON.parse(localStorage.getItem(DATA_KEYS.USER_PROFILE)) || {};
    
    // Check for duplicates
    const activityNames = activities.map(a => a.name);
    const uniqueActivityNames = [...new Set(activityNames)];
    const duplicateActivities = activityNames.length - uniqueActivityNames.length;
    
    const collegeNames = colleges.map(c => c.name);
    const uniqueCollegeNames = [...new Set(collegeNames)];
    const duplicateColleges = collegeNames.length - uniqueCollegeNames.length;
    
    return {
      activities: {
        total: activities.length,
        unique: uniqueActivityNames.length,
        duplicates: duplicateActivities,
        hasData: activities.length > 0
      },
      colleges: {
        total: colleges.length,
        unique: uniqueCollegeNames.length,
        duplicates: duplicateColleges,
        hasData: colleges.length > 0
      },
      academicProfile: {
        hasData: Object.keys(academicProfile).length > 0,
        fields: Object.keys(academicProfile)
      },
      userProfile: {
        hasData: Object.keys(userProfile).length > 0,
        fields: Object.keys(userProfile)
      }
    };
  } catch (error) {
    console.error('[DataCleanup] Error getting data stats:', error);
    return {
      error: error.message
    };
  }
};

/**
 * Clear all user data (nuclear option)
 * @returns {boolean} Success status
 */
export const clearAllData = () => {
  try {
    Object.values(DATA_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log('[DataCleanup] All user data cleared');
    return true;
  } catch (error) {
    console.error('[DataCleanup] Error clearing all data:', error);
    return false;
  }
};

/**
 * Clear all activities from localStorage
 * @returns {object} Cleanup results
 */
export const clearAllActivities = () => {
  try {
    localStorage.removeItem(DATA_KEYS.ACTIVITIES);
    console.log('[DataCleanup] All activities cleared from localStorage');
    
    // Dispatch event to notify components
    window.dispatchEvent(new CustomEvent('collegeActivitiesUpdated'));
    
    return {
      success: true,
      message: 'All activities cleared successfully'
    };
  } catch (error) {
    console.error('[DataCleanup] Error clearing activities:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Initialize empty activities state for new users
 * @returns {boolean} Success status
 */
export const initializeEmptyActivities = () => {
  try {
    const existingActivities = localStorage.getItem(DATA_KEYS.ACTIVITIES);
    
    // Only initialize if no activities exist or if it's corrupted
    if (!existingActivities || existingActivities === 'null' || existingActivities === 'undefined') {
      localStorage.setItem(DATA_KEYS.ACTIVITIES, JSON.stringify([]));
      console.log('[DataCleanup] Initialized empty activities state');
      return true;
    }
    
    // If activities exist, validate they're in the correct format
    try {
      const parsed = JSON.parse(existingActivities);
      if (!Array.isArray(parsed)) {
        localStorage.setItem(DATA_KEYS.ACTIVITIES, JSON.stringify([]));
        console.log('[DataCleanup] Fixed corrupted activities data');
        return true;
      }
    } catch (parseError) {
      localStorage.setItem(DATA_KEYS.ACTIVITIES, JSON.stringify([]));
      console.log('[DataCleanup] Fixed corrupted activities data');
      return true;
    }
    
    return true;
  } catch (error) {
    console.error('[DataCleanup] Error initializing activities:', error);
    return false;
  }
};

/**
 * Force clear all activities and reset to empty state
 * This ensures no activities are shown by default for any user
 * @returns {object} Cleanup results
 */
export const forceClearAllActivities = () => {
  try {
    // Remove activities from localStorage
    localStorage.removeItem(DATA_KEYS.ACTIVITIES);
    
    // Initialize with empty array
    localStorage.setItem(DATA_KEYS.ACTIVITIES, JSON.stringify([]));
    
    console.log('[DataCleanup] Force cleared all activities and reset to empty state');
    
    // Dispatch event to notify all components
    window.dispatchEvent(new CustomEvent('collegeActivitiesUpdated', {
      detail: { activities: [] }
    }));
    
    return {
      success: true,
      message: 'All activities cleared and reset to empty state'
    };
  } catch (error) {
    console.error('[DataCleanup] Error force clearing activities:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  window.dataCleanup = {
    removeDuplicateActivities,
    removeDuplicateColleges,
    cleanupAllData,
    getDataStats,
    clearAllData,
    clearAllActivities,
    initializeEmptyActivities,
    forceClearAllActivities
  };
  
  console.log('🧹 Data cleanup utilities loaded!');
  console.log('Available functions:');
  console.log('  window.dataCleanup.getDataStats() - Get data statistics');
  console.log('  window.dataCleanup.removeDuplicateActivities() - Remove duplicate activities');
  console.log('  window.dataCleanup.removeDuplicateColleges() - Remove duplicate colleges');
  console.log('  window.dataCleanup.cleanupAllData() - Clean up all data');
  console.log('  window.dataCleanup.clearAllData() - Clear all data (nuclear option)');
  console.log('  window.dataCleanup.clearAllActivities() - Clear all activities');
  console.log('  window.dataCleanup.initializeEmptyActivities() - Initialize empty activities');
  console.log('  window.dataCleanup.forceClearAllActivities() - Force clear all activities and reset to empty state');
} 